import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkMath from 'remark-math';
import rehypeKatex from 'rehype-katex';
import 'katex/dist/katex.min.css';
import GlossaryTerm from '../../glossary/GlossaryTerm';
import { rehypeGlossary } from '../../glossary/rehypeGlossary';

interface MarkdownProps {
  children: string;
  // `compact` tightens spacing for AI answer bubbles vs. full slide bodies.
  compact?: boolean;
  className?: string;
  // When true, auto-link known technical terms in plain text to the interactive
  // <GlossaryTerm> (speaker + pronunciation popover). Opt-in: slide bodies use
  // it; AI answer bubbles leave it off by default.
  glossary?: boolean;
}

// Shared markdown renderer for slide bodies and AI answers. GFM tables/lists +
// KaTeX for chemistry formulas and equations ($…$ inline, $$…$$ block). Styling
// is bound to ws- tokens so it themes across paper/light/dark. We deliberately
// avoid @tailwindcss/typography (not a dep) and style elements directly.
export default function Markdown({
  children,
  compact = false,
  className = '',
  glossary = false,
}: MarkdownProps) {
  const gap = compact ? 'space-y-2 text-sm' : 'space-y-4';
  // KaTeX must run before the glossary pass so we never rewrite math output; the
  // glossary pass also explicitly skips .katex/.math subtrees as a belt-and-braces.
  const rehypePlugins = glossary
    ? [rehypeKatex, rehypeGlossary]
    : [rehypeKatex];
  return (
    <div className={`lecture-md ${gap} text-ws-ink-2 leading-relaxed ${className}`}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm, remarkMath]}
        rehypePlugins={rehypePlugins}
        components={{
          // Custom element emitted by rehypeGlossary -> interactive inline term.
          // (Cast: react-markdown's component map is typed for known HTML tags.)
          ...({
            'glossary-term': ({ node, children: c, ...p }: any) => (
              <GlossaryTerm
                slug={node?.properties?.dataSlug as string | undefined}
                text={node?.properties?.dataText as string | undefined}
                {...p}
              >
                {c}
              </GlossaryTerm>
            ),
          } as Record<string, unknown>),
          h1: ({ node, ...p }) => (
            <h1 className="text-2xl font-semibold text-ws-ink" {...p} />
          ),
          h2: ({ node, ...p }) => (
            <h2 className="text-xl font-semibold text-ws-ink mt-2" {...p} />
          ),
          h3: ({ node, ...p }) => (
            <h3 className="text-base font-semibold text-ws-ink mt-1" {...p} />
          ),
          p: ({ node, ...p }) => <p {...p} />,
          ul: ({ node, ...p }) => <ul className="list-disc pl-5 space-y-1.5" {...p} />,
          ol: ({ node, ...p }) => <ol className="list-decimal pl-5 space-y-1.5" {...p} />,
          li: ({ node, ...p }) => <li className="marker:text-ws-ink-4" {...p} />,
          a: ({ node, ...p }) => (
            <a
              className="text-ws-accent underline underline-offset-2 hover:text-ws-accent-hover"
              target="_blank"
              rel="noreferrer"
              {...p}
            />
          ),
          strong: ({ node, ...p }) => (
            <strong className="font-semibold text-ws-ink" {...p} />
          ),
          code: ({ node, className: cn, ...p }) => {
            const isBlock = /language-/.test(cn || '');
            return isBlock ? (
              <code
                className={`block rounded-md bg-ws-surface-2 p-3 font-mono text-[13px] overflow-x-auto ${cn || ''}`}
                {...p}
              />
            ) : (
              <code
                className="rounded bg-ws-surface-2 px-1.5 py-0.5 font-mono text-[0.85em] text-ws-ink"
                {...p}
              />
            );
          },
          pre: ({ node, ...p }) => <pre className="overflow-x-auto" {...p} />,
          blockquote: ({ node, ...p }) => (
            <blockquote
              className="border-l-2 border-ws-accent pl-4 italic text-ws-ink-3"
              {...p}
            />
          ),
          table: ({ node, ...p }) => (
            <div className="overflow-x-auto">
              <table className="w-full text-sm border-collapse" {...p} />
            </div>
          ),
          th: ({ node, ...p }) => (
            <th
              className="border border-ws-border bg-ws-surface-2 px-2.5 py-1.5 text-left font-semibold text-ws-ink"
              {...p}
            />
          ),
          td: ({ node, ...p }) => (
            <td className="border border-ws-border px-2.5 py-1.5" {...p} />
          ),
          hr: () => <hr className="border-ws-border" />,
        }}
      >
        {children}
      </ReactMarkdown>
    </div>
  );
}
